/**
 * Certificate Viewer Component
 * Modal to view medical certificates (PDF/images)
 *
 * @version 2025.1.0
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { MedicalLeave, CertificateInfo } from '../../../types/medicalLeave';
import { medicalLeaveService } from '../../../services/medicalLeaveService';

interface CertificateViewerProps {
  open: boolean;
  onClose: () => void;
  medicalLeave: MedicalLeave | null;
}

export const CertificateViewer: React.FC<CertificateViewerProps> = ({
  open,
  onClose,
  medicalLeave,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [certificateInfo, setCertificateInfo] = useState<CertificateInfo | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);

  // Cada abertura é um pedido: se o modal fechou ou a licença mudou antes de a resposta chegar,
  // ela é descartada — senão o atestado de outra pessoa apareceria na licença aberta.
  // Sem nada a carregar (modal fechado ou licença sem atestado), nada de antes fica na tela:
  // nem o arquivo anterior, nem o indicador de uma carga que foi cancelada.
  useEffect(() => {
    const leaveId = medicalLeave?.id;
    if (!open || !leaveId || !medicalLeave.hasCertificate) {
      setLoading(false);
      setError(null);
      setCertificateInfo(null);
      setFileUrl(null);
      return undefined;
    }

    let cancelled = false;
    const loadCertificate = async () => {
      try {
        setLoading(true);
        setError(null);
        setFileUrl(null);
        const info = await medicalLeaveService.getCertificate(leaveId);
        const file = info ? await medicalLeaveService.fetchCertificateFile(leaveId) : null;
        if (cancelled) return;
        setCertificateInfo(info);
        setFileUrl(file ? URL.createObjectURL(file) : null);
      } catch (err: unknown) {
        if (cancelled) return;
        setError(err instanceof Error && err.message ? err.message : 'Erro ao carregar atestado');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadCertificate();

    return () => {
      cancelled = true;
    };
  }, [open, medicalLeave]);

  // A URL local segura o arquivo em memória até ser liberada: trocar de arquivo, fechar ou desmontar a solta.
  useEffect(() => {
    if (!fileUrl) return undefined;
    return () => URL.revokeObjectURL(fileUrl);
  }, [fileUrl]);

  const handleDownload = () => {
    if (!fileUrl) return;
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = certificateInfo?.originalName || certificateInfo?.fileName || 'atestado';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // Abre no próprio clique, com o arquivo já carregado — abrir depois de um await cai no bloqueador de pop-up.
  const handleOpenInNewTab = () => {
    if (!fileUrl) return;
    window.open(fileUrl, '_blank');
  };

  const isImage = certificateInfo?.fileExtension &&
    ['jpg', 'jpeg', 'png', 'gif'].includes(certificateInfo.fileExtension.toLowerCase());

  const isPdf = certificateInfo?.fileExtension?.toLowerCase() === 'pdf';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Atestado Médico - {medicalLeave?.employeeName}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && !medicalLeave?.hasCertificate && (
          <Alert severity="info">
            Esta licença médica não possui atestado anexado.
          </Alert>
        )}

        {!loading && !error && certificateInfo && fileUrl && (
          <Box sx={{ minHeight: 400 }}>
            {isImage && (
              <Box sx={{ textAlign: 'center' }}>
                <img
                  src={fileUrl}
                  alt="Atestado Médico"
                  style={{ maxWidth: '100%', maxHeight: '70vh' }}
                />
              </Box>
            )}

            {isPdf && (
              <Box sx={{ height: '70vh', width: '100%' }}>
                <iframe
                  src={fileUrl}
                  title="Atestado Médico"
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                />
              </Box>
            )}

            {!isImage && !isPdf && (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="body1" gutterBottom>
                  Arquivo: {certificateInfo.fileName}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Este tipo de arquivo não pode ser visualizado diretamente.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                  sx={{ mt: 2 }}
                >
                  Baixar Arquivo
                </Button>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {certificateInfo && fileUrl && (
          <>
            <Button
              startIcon={<OpenInNewIcon />}
              onClick={handleOpenInNewTab}
            >
              Abrir em Nova Aba
            </Button>
            <Button
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
            >
              Download
            </Button>
          </>
        )}
        <Button onClick={onClose} variant="outlined">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CertificateViewer;
